import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Gauge } from '@mui/x-charts';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Iconify from '../../../../theme/iconify/iconify';
import { useResponsive } from '../../../../hooks/use-responsive';
import { fDate, fTime, fStopwatch } from '../../../util/format-time';

function Result({ results, user }) {
    const { t } = useTranslation();
    const mdUp = useResponsive('up', 'md');

    const percentage = (score, total) => {
        if (total === 0) return 0;
        return Math.round((score / total) * 100);
    }

    const answered = (questions) => {
        let answered = 0;
        questions?.forEach(question => {
            if (question?.answers?.length > 0) {
                answered++;
            }
        });
        return answered;
    }

    return (
        <>
            <Grid container sx={{ mt: '0px !important' }}>
                <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography variant='h6' sx={{ fontSize: '1rem !important' }}>
                        {t('course.exam.timeline.title')}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                        <Swiper
                            spaceBetween={8}
                            slidesPerView={mdUp ? 2 : 1}
                            modules={[Pagination]}
                            pagination={{ clickable: true }}
                            className={results.length > 2 && 'mISLec_swiper_pagination'}
                        >
                            {results?.map((result, index) => (
                                <SwiperSlide key={index}>
                                    <Grid container sx={{ py: 1, px: 2, mt: 1, border: '1px solid rgba(33, 43, 54, 0.12)', borderRadius: '8px', background: '#FFFFFF' }}>
                                        <Grid item xs={12} key={index}>
                                            <Stack>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6} md={4} sx={{ fontSize: '0.875rem !important' }}>
                                                        <Typography variant='caption' sx={{ fontWeight: '600', color: 'text.disabled' }}>
                                                            {t('course.exam.timeline.score')}
                                                        </Typography>
                                                        <Box display='flex' alignItems='center'>
                                                            <Box
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    overflow: 'hidden',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Gauge
                                                                        width={60}
                                                                        height={60}
                                                                        value={percentage(result?.quiz?.user_points, result?.quiz?.total_points)}
                                                                        startAngle={-90}
                                                                        endAngle={90}
                                                                        text={''}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            <Typography variant='h6' sx={{ ml: 1, fontSize: '1.25rem !important', fontWeight: '600', color: 'text.primary' }}>
                                                                {percentage(result?.quiz?.user_points, result?.quiz?.total_points)}%
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6} md={4} sx={{ fontSize: '0.875rem !important' }} >
                                                        <Typography variant='caption' sx={{ fontWeight: '600', color: 'text.disabled' }}>
                                                            {t('course.exam.timeline.points')}
                                                        </Typography>

                                                        <Box display='flex' alignItems='center' sx={{ height: '40px !important' }}>
                                                            <Iconify icon='octicon:goal-16' width={22} sx={{ mr: 1, color: 'primary.main' }} />
                                                            <Typography variant='h6' sx={{ fontSize: '1.25rem !important', fontWeight: '600', color: 'text.primary' }}>
                                                                {result?.quiz?.user_points?.toFixed(2)}
                                                                <Box component='span' sx={{ fontSize: '0.75rem !important', color: 'text.disabled' }}>/{result?.quiz?.total_points}</Box>
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={6} md={4} sx={{ fontSize: '0.875rem !important' }} >
                                                        <Typography variant='caption' sx={{ fontWeight: '600', color: 'text.disabled' }}>
                                                            {t('course.exam.timeline.answered')}
                                                        </Typography>

                                                        <Box display='flex' alignItems='center' sx={{ height: '40px !important' }}>
                                                            <Iconify icon='mingcute:checkbox-line' width={22} sx={{ mr: 1, color: 'primary.main' }} />
                                                            <Typography variant='h6' sx={{ fontSize: '1.25rem !important', fontWeight: '600', color: 'text.primary' }}>
                                                                {answered(result?.quiz?.questions)}
                                                                <Box component='span' sx={{ fontSize: '0.75rem !important', color: 'text.disabled' }}>/{result?.original_quiz?.no_questions}</Box>
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, fontSize: '0.875rem !important', color: 'text.disabled' }}>
                                                <Iconify icon='f7:calendar-today' width={18} sx={{ mr: 0.5 }} />
                                                <Typography variant='caption'>
                                                    {fDate(result?.quiz?.end_time, user.language, Intl.DateTimeFormat().resolvedOptions().timeZone)}, {fTime(result?.quiz?.end_time, user.language, Intl.DateTimeFormat().resolvedOptions().timeZone)}
                                                </Typography>
                                                <Divider orientation='vertical' flexItem sx={{ mx: 1, my: 0.1 }} />
                                                <Iconify icon='wi:time-9' width={18} sx={{ mr: 0.5 }} />
                                                <Typography variant='caption'>
                                                    {fStopwatch(Math.floor((new Date(result?.quiz?.end_time) - new Date(result?.quiz?.start_time)) / 1000), user.language)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}

export default Result;